import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { endpoints } from "../lib/api";
import { Sparkles, Loader2, ArrowRight, Bookmark, CheckCircle2, X, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const TONES = [
  { value: "futuristic", label: "Futuristic" },
  { value: "luxury", label: "Luxury" },
  { value: "minimal", label: "Minimal" },
  { value: "enterprise", label: "Enterprise" },
  { value: "gen-z", label: "Gen Z" },
  { value: "trustworthy", label: "Trustworthy" },
];

function ScoreBadge({ label, value }) {
  const color = value >= 90 ? "emerald" : value >= 80 ? "indigo" : value >= 70 ? "amber" : "zinc";
  const cls = {
    emerald: "bg-emerald-50 text-emerald-700",
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    zinc: "bg-zinc-100 text-zinc-700",
  }[color];
  return (
    <div className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {label} {value}
    </div>
  );
}

export default function AIFinder() {
  const [idea, setIdea] = useState("");
  const [industry, setIndustry] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("minimal");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);
  const { user, requestLogin } = useAuth();

  const refreshQuota = async () => {
    try {
      const r = await endpoints.aiQuota();
      setQuota(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refreshQuota(); }, [user]);

  const run = async (e) => {
    e.preventDefault();
    if (!idea.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await endpoints.generateNames({ idea, industry, keywords, tone });
      setResults(res.data.names || []);
      refreshQuota();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402) {
        setError(detail || "You've used all 5 free AI Name Finder searches this month. Upgrade to Pro for unlimited searches.");
        refreshQuota();
      } else {
        setError(detail || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const save = async (domain) => {
    if (!user) {
      toast("Sign in to save", { description: "Free with Google — takes 2 seconds." });
      requestLogin("/dashboard");
      return;
    }
    try {
      await endpoints.savedCreate({ domain, source: "ai" });
      toast.success(`Saved ${domain}`);
    } catch (e) { toast.error("Could not save"); }
  };

  return (
    <Layout>
      <Seo
        title="AI domain name generator"
        description="Generate brandable startup domain names with AI. Set tone, industry and keywords, then compare live prices across various hosts in one click."
        keywords="ai domain name generator, startup domain names, brandable domains, ai domain finder"
        path="/ai-finder"
      />
      <section data-testid="ai-finder-page">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">AI Name Studio</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
          Brandable names <span className="text-indigo-600">in seconds</span>.
        </h1>
        <p className="mt-3 max-w-2xl text-zinc-600">
          Powered by AI. We score each name on memorability and brand fit, then check availability live.
        </p>

        <form onSubmit={run} className="mt-8 grid gap-4 rounded-2xl border border-zinc-200 bg-white p-6 md:grid-cols-2 soft-shadow" data-testid="ai-form">
          <div className="md:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Business idea</label>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              rows={3}
              required
              data-testid="ai-idea-input"
              placeholder="A platform that helps indie SaaS founders launch their first paid product…"
              className="mt-2 w-full rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Industry</label>
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              data-testid="ai-industry-input"
              placeholder="Fintech, hotel tech, AI tools…"
              className="mt-2 w-full rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Keywords</label>
            <input
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              data-testid="ai-keywords-input"
              placeholder="forge, mesh, signal…"
              className="mt-2 w-full rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Tone</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  type="button"
                  key={t.value}
                  onClick={() => setTone(t.value)}
                  data-testid={`tone-${t.value}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                    tone === t.value
                      ? "bg-zinc-900 text-white"
                      : "border border-zinc-200 bg-white text-zinc-700 hover:border-indigo-200 hover:text-indigo-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-3">
            <div className="text-xs text-zinc-500" data-testid="ai-quota">
              {quota?.is_pro ? (
                <span className="inline-flex items-center gap-1 font-medium text-indigo-700">
                  <Crown className="h-3.5 w-3.5" /> Pro · unlimited searches
                </span>
              ) : quota ? (
                <>
                  <span className="font-medium text-zinc-700">{quota.used}/{quota.limit}</span> free searches used this month
                  {quota.remaining === 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                      Limit reached
                    </span>
                  )}
                </>
              ) : (
                <>Free plan · 5 searches per month</>
              )}
            </div>
            <button
              type="submit"
              disabled={loading || !idea.trim() || (quota && !quota.is_pro && quota.remaining === 0)}
              data-testid="ai-generate-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Generate names
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800" data-testid="ai-error">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" /> {error}
            </div>
            <div className="flex gap-2">
              {!user && (
                <button onClick={() => requestLogin("/ai-finder")} className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100">
                  Sign in
                </button>
              )}
              <Link
                to="/pricing"
                data-testid="ai-error-upgrade"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-900/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-900"
              >
                <Crown className="h-3.5 w-3.5" /> Upgrade to Pro
              </Link>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-12" data-testid="ai-results">
            <div className="mb-5 flex items-end justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Results</div>
                <h2 className="mt-1 font-display text-2xl font-bold text-zinc-900">{results.length} name candidates</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {results.map((n) => (
                <div
                  key={n.domain}
                  data-testid={`name-${n.domain}`}
                  className="group rounded-2xl border border-zinc-100 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md soft-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-xl font-semibold text-zinc-900">{n.domain}</div>
                      <p className="mt-1.5 text-sm leading-relaxed text-zinc-600">{n.rationale}</p>
                    </div>
                    {n.available === true && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> Available
                      </span>
                    )}
                    {n.available === false && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">
                        <X className="h-3 w-3" /> Taken
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <ScoreBadge label="Brand" value={n.brand_score} />
                    <ScoreBadge label="Memo" value={n.memorability} />
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Link
                      to={`/compare?name=${encodeURIComponent(n.domain)}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800"
                      data-testid={`compare-${n.domain}`}
                    >
                      Compare prices <ArrowRight className="h-3 w-3" />
                    </Link>
                    <button
                      onClick={() => save(n.domain)}
                      className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-900 hover:border-indigo-200 hover:text-indigo-700"
                      data-testid={`save-${n.domain}`}
                    >
                      <Bookmark className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
